<script setup lang="ts">
import { Send, Loader2, X, Trash2, History, Plus } from 'lucide-vue-next';
import { useChat } from '~/composables/useChat';
import { useAuth } from '~/composables/useAuth';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { z } from 'zod';
import { withoutTrailingSlash, withLeadingSlash } from 'ufo';

const route = useRoute();

interface Props {
	avatarUrl?: string | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
	close: [];
}>();

const { isAuthenticated, accessToken } = useAuth();
const { 
	session, 
	messages, 
	isLoading, 
	isInitializing, 
	error, 
	conversations,
	isLoadingConversations,
	initializeSession, 
	loadLastConversation,
	loadConversationsList,
	loadConversation,
	createNewConversation,
	sendMessage, 
	resetChat 
} = useChat();

// Mostra il form solo se non c'è una sessione attiva E l'utente non è autenticato
// Se l'utente è autenticato, può inviare direttamente un messaggio (che creerà automaticamente la conversazione)
const showForm = computed(() => !session.value && !isAuthenticated.value);
const showConversationsList = ref(false);
const messageInput = ref('');
const formData = reactive({
	full_name: '',
	email: '',
	privacy_consent: false,
});

const formSchema = z.object({
	full_name: z.string().min(1, 'Il nome è obbligatorio'),
	email: z.string().email('Email non valida'),
	privacy_consent: z.boolean().refine((val) => val === true, {
		message: 'Devi accettare la privacy policy per continuare',
	}),
});

const formErrors = reactive({
	full_name: '',
	email: '',
	privacy_consent: '',
});

const messagesContainer = ref<HTMLElement | null>(null);
const showDeleteDialog = ref(false);

// Auto-scroll to bottom when new messages arrive
watch(messages, () => {
	nextTick(() => {
		if (messagesContainer.value) {
			messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
		}
	});
}, { deep: true });

// Also scroll when loading state changes (new message being sent)
watch(isLoading, () => {
	if (!isLoading.value) {
		nextTick(() => {
			if (messagesContainer.value) {
				messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
			}
		});
	}
});

const handleSubmitForm = async () => {
	// Reset errors
	formErrors.full_name = '';
	formErrors.email = '';
	formErrors.privacy_consent = '';

	// Validate
	const result = formSchema.safeParse(formData);
	if (!result.success) {
		result.error.errors.forEach((err) => {
			if (err.path[0] === 'full_name') {
				formErrors.full_name = err.message;
			}
			if (err.path[0] === 'email') {
				formErrors.email = err.message;
			}
			if (err.path[0] === 'privacy_consent') {
				formErrors.privacy_consent = err.message;
			}
		});
		return;
	}

	try {
		await initializeSession(formData.full_name, formData.email, formData.privacy_consent);
	} catch (err) {
		// Error is handled by the composable
		console.error('Failed to initialize session:', err);
	}
};

const handleSendMessage = async () => {
	if (!messageInput.value.trim() || isLoading.value) return;

	const content = messageInput.value.trim();
	messageInput.value = '';

	// Get current page permalink
	const currentPage = withoutTrailingSlash(withLeadingSlash(route.path));

	try {
		await sendMessage(content, currentPage);
	} catch (err) {
		// Error is handled by the composable
		console.error('Failed to send message:', err);
		// Restore message on error
		messageInput.value = content;
	}
};

const handleKeyPress = (e: KeyboardEvent) => {
	if (e.key === 'Enter' && !e.shiftKey) {
		e.preventDefault();
		handleSendMessage();
	}
};

const handleDeleteClick = () => {
	showDeleteDialog.value = true;
};

const confirmDelete = () => {
	resetChat();
	emit('close');
	showDeleteDialog.value = false;
};

// Carica automaticamente l'ultima conversazione se l'utente è autenticato
onMounted(async () => {
	// Verifica se c'è un token nel localStorage anche se lo stato non è ancora sincronizzato
	const token = process.client ? localStorage.getItem('directus_access_token') : null;
	const shouldLoad = (isAuthenticated.value && accessToken?.value) || (token && !session.value);
	
	if (shouldLoad) {
		try {
			await loadLastConversation();
		} catch (err) {
			console.error('Failed to load last conversation:', err);
		}
	}
});

// Watcher per gestire i cambiamenti di autenticazione
watch(isAuthenticated, async (newValue, oldValue) => {
	// Se l'utente ha fatto login (da non autenticato a autenticato)
	if (newValue && !oldValue) {
		// Resetta lo stato del chatbot
		resetChat();
		// Chiudi la lista delle conversazioni se aperta
		showConversationsList.value = false;
		// Carica l'ultima conversazione
		try {
			await loadLastConversation();
		} catch (err) {
			console.error('Failed to load last conversation after login:', err);
		}
	}
	// Se l'utente ha fatto logout (da autenticato a non autenticato)
	else if (!newValue && oldValue) {
		// Resetta completamente lo stato del chatbot (inclusa la lista delle conversazioni)
		resetChat();
		// Chiudi la lista delle conversazioni se aperta
		showConversationsList.value = false;
	}
}, { immediate: false });

// Gestisce il click sull'icona delle conversazioni
const handleConversationsClick = async () => {
	if (!showConversationsList.value) {
		// Carica l'elenco delle conversazioni
		try {
			await loadConversationsList();
			showConversationsList.value = true;
		} catch (err) {
			console.error('Failed to load conversations:', err);
		}
	} else {
		showConversationsList.value = false;
	}
};

// Gestisce la selezione di una conversazione
const handleSelectConversation = async (conversationId: number) => {
	try {
		await loadConversation(conversationId);
		showConversationsList.value = false;
	} catch (err) {
		console.error('Failed to load conversation:', err);
	}
};

// Gestisce la creazione di una nuova conversazione
const handleCreateNewConversation = async () => {
	try {
		await createNewConversation();
		showConversationsList.value = false;
	} catch (err) {
		console.error('Failed to create new conversation:', err);
	}
};

// Formatta la data per l'anteprima
const formatDate = (dateString: string | null) => {
	if (!dateString) return '';
	const date = new Date(dateString);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / 60000);
	const diffHours = Math.floor(diffMs / 3600000);
	const diffDays = Math.floor(diffMs / 86400000);

	if (diffMins < 1) return 'Adesso';
	if (diffMins < 60) return `${diffMins} min fa`;
	if (diffHours < 24) return `${diffHours} ore fa`;
	if (diffDays < 7) return `${diffDays} giorni fa`;
	return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
};
</script>

<template>
	<div class="flex flex-col h-full">
		<!-- Header -->
		<div class="flex items-center justify-between p-6 border-b border-gray-700 bg-black">
			<div class="flex items-center gap-4">
				<img
					v-if="props.avatarUrl"
					:src="props.avatarUrl"
					alt="Chatbot Avatar"
					class="w-8 h-8 object-contain flex-shrink-0"
				/>
				<div>
					<h3 class="font-semibold text-lg text-white">WeFocus Assistente Virtuale</h3>
				</div>
			</div>
			<div class="flex items-center gap-2">
				<Button 
					v-if="isAuthenticated && !showForm" 
					variant="ghost" 
					size="icon" 
					@click="handleConversationsClick" 
					aria-label="Mostra conversazioni" 
					class="text-white hover:bg-gray-800"
				>
					<History :size="18" />
				</Button>
				<Button 
					v-if="!showForm" 
					variant="ghost" 
					size="icon" 
					@click="handleDeleteClick" 
					aria-label="Elimina conversazione" 
					class="text-white hover:bg-gray-800"
				>
					<Trash2 :size="18" />
				</Button>
			</div>
		</div>

		<!-- Form (Initial) -->
		<div v-if="showForm" class="flex-1 flex items-start p-6">
			<div class="w-full max-w-sm space-y-4">
				<div>
					<h4 class="text-lg font-semibold mb-2">Inizia una conversazione</h4>
					<p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
						Sono qui per aiutarti se hai dubbi sui nostri percorsi o servizi.
					</p>
					<p class="text-sm text-gray-600 dark:text-gray-400">
						Inserisci il tuo nome e la tua email per iniziare a chattare con il nostro assistente virtuale.
					</p>
				</div>

				<form @submit.prevent="handleSubmitForm" class="space-y-4">
					<div>
						<label for="full_name" class="block text-sm font-medium mb-1">Nome</label>
						<Input
							id="full_name"
							v-model="formData.full_name"
							placeholder="Il tuo nome"
							:disabled="isInitializing"
							:class="{ 'border-red-500': formErrors.full_name }"
						/>
						<p v-if="formErrors.full_name" class="text-sm text-red-500 mt-1">{{ formErrors.full_name }}</p>
					</div>

					<div>
						<label for="email" class="block text-sm font-medium mb-1">Email</label>
						<Input
							id="email"
							v-model="formData.email"
							type="email"
							placeholder="tua@email.com"
							:disabled="isInitializing"
							:class="{ 'border-red-500': formErrors.email }"
						/>
						<p v-if="formErrors.email" class="text-sm text-red-500 mt-1">{{ formErrors.email }}</p>
					</div>

					<div>
						<div class="flex items-start gap-2">
							<Checkbox
								id="privacy_consent"
								v-model:checked="formData.privacy_consent"
								:disabled="isInitializing"
								:class="{ 'border-red-500': formErrors.privacy_consent }"
							/>
							<label for="privacy_consent" class="text-sm leading-5 cursor-pointer">
								Accetto la
								<a href="/privacy-policy" target="_blank" class="font-bold text-black underline hover:text-black/80">
									privacy policy
								</a>
								<span class="text-500">*</span>
							</label>
						</div>
						<p v-if="formErrors.privacy_consent" class="text-sm text-red-500 mt-1">{{ formErrors.privacy_consent }}</p>
					</div>

					<Button type="submit" class="w-full" :disabled="isInitializing">
						<Loader2 v-if="isInitializing" class="mr-2 h-4 w-4 animate-spin" />
						{{ isInitializing ? 'Inizializzazione...' : 'INIZIA CONVERSAZIONE' }}
					</Button>
				</form>

				<p v-if="error" class="text-sm text-red-500 mt-2">{{ error }}</p>
			</div>
		</div>

		<!-- Chat Interface (mostrata anche se utente autenticato senza conversazione) -->
		<div v-if="!showForm || isAuthenticated" class="flex-1 flex flex-col min-h-0 overflow-hidden relative">
			<!-- Conversations List Overlay -->
			<div
				v-if="showConversationsList"
				class="absolute inset-0 bg-white dark:bg-gray-900 z-10 flex flex-col"
			>
				<div class="p-4 border-b border-gray-200 dark:border-gray-800">
					<div class="flex items-center justify-between">
						<h4 class="font-semibold text-lg">Le tue conversazioni</h4>
						<div class="flex items-center gap-2">
							<Button
								variant="ghost"
								size="icon"
								@click="handleCreateNewConversation"
								aria-label="Nuova conversazione"
								:disabled="isInitializing"
								class="text-primary hover:bg-primary/10"
							>
								<Plus :size="18" />
							</Button>
							<Button
								variant="ghost"
								size="icon"
								@click="showConversationsList = false"
								aria-label="Chiudi"
							>
								<X :size="18" />
							</Button>
						</div>
					</div>
				</div>
				<div class="flex-1 overflow-y-auto p-4">
					<div v-if="isLoadingConversations" class="flex items-center justify-center py-8">
						<Loader2 class="h-6 w-6 animate-spin text-gray-500" />
					</div>
					<div v-else-if="conversations.length === 0" class="text-center py-8 text-gray-500">
						<p>Nessuna conversazione trovata</p>
					</div>
					<div v-else class="space-y-2">
						<button
							v-for="conv in conversations"
							:key="conv.id"
							@click="handleSelectConversation(conv.id)"
							class="w-full text-left p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
							:class="{ 'bg-gray-50 dark:bg-gray-800': session?.conversation_id === conv.id }"
						>
							<div class="flex items-start justify-between gap-2">
								<div class="flex-1 min-w-0">
									<h5 class="font-medium text-sm truncate">{{ conv.title }}</h5>
									<p v-if="conv.last_message" class="text-xs text-gray-500 mt-1 line-clamp-2">
										{{ conv.last_message.content }}
									</p>
									<p class="text-xs text-gray-400 mt-2">
										{{ formatDate(conv.date_updated || conv.date_created) }}
									</p>
								</div>
							</div>
						</button>
					</div>
				</div>
			</div>

			<!-- Messages -->
			<div
				ref="messagesContainer"
				class="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-950 min-h-0"
				:class="{ 'opacity-50 pointer-events-none': showConversationsList }"
			>
				<!-- Messaggio di benvenuto se utente autenticato senza conversazione -->
				<div
					v-if="isAuthenticated && !session && messages.length === 0 && !isLoading"
					class="flex justify-center items-center h-full"
				>
					<div class="text-center max-w-md px-4">
						<p class="text-gray-600 dark:text-gray-400">
							Ciao! Sono qui per aiutarti. Inizia a scrivere un messaggio per avviare la conversazione.
						</p>
					</div>
				</div>

				<div
					v-for="message in messages"
					:key="message.id"
					class="flex"
					:class="message.role === 'user' ? 'justify-end' : 'justify-start'"
				>
					<div
						class="max-w-[80%] rounded-lg px-4 py-2"
						:class="
							message.role === 'user'
								? 'bg-primary text-primary-foreground'
								: 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700'
						"
					>
						<p class="text-sm whitespace-pre-wrap">{{ message.content }}</p>
						<p
							class="text-xs mt-1 opacity-70"
						>
							{{ new Date(message.date_created).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) }}
						</p>
					</div>
				</div>

				<div v-if="isLoading" class="flex justify-start">
					<div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2">
						<Loader2 class="h-4 w-4 animate-spin text-gray-500" />
					</div>
				</div>
			</div>

			<!-- Error Message -->
			<div v-if="error" class="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
				<p class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>
			</div>

			<!-- Input -->
			<div class="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
				<form @submit.prevent="handleSendMessage" class="flex gap-2 items-center">
					<Textarea
						v-model="messageInput"
						placeholder="Scrivi un messaggio..."
						class="flex-1 min-h-[60px] max-h-[120px] resize-none"
						:disabled="isLoading"
						@keydown="handleKeyPress"
					/>
					<Button type="submit" size="icon" :disabled="!messageInput.trim() || isLoading">
						<Loader2 v-if="isLoading" class="h-4 w-4 animate-spin" />
						<Send v-else class="h-4 w-4" />
					</Button>
				</form>
			</div>
		</div>

		<!-- Delete Confirmation Dialog -->
		<Dialog v-model:open="showDeleteDialog">
			<DialogContent class="!rounded-none sm:!rounded-none !left-4 !right-4 !translate-x-0 sm:!left-1/2 sm:!right-auto sm:!-translate-x-1/2 max-w-[calc(100vw-2rem)] sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Elimina conversazione</DialogTitle>
					<DialogDescription>
						Sei sicuro di voler eliminare questa conversazione? Questa azione non può essere annullata.
					</DialogDescription>
				</DialogHeader>
				<DialogFooter class="flex-col gap-3 sm:flex-row sm:gap-2">
					<Button variant="outline" @click="showDeleteDialog = false" class="w-full sm:w-auto">
						ANNULLA
					</Button>
					<Button @click="confirmDelete" class="w-full sm:w-auto">
						ELIMINA
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</div>
</template>

